import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, LocateFixed, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

const DEFAULT = [-0.0263, 109.3425]; // Pontianak / Kubu Raya

function CenterTracker({ onMove }) {
  const ref = useRef(onMove);
  ref.current = onMove;
  const map = useMapEvents({ move: () => ref.current(map.getCenter()), moveend: () => ref.current(map.getCenter()) });
  useEffect(() => { ref.current(map.getCenter()); }, [map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, Math.max(map.getZoom(), 17), { duration: 0.8 }); }, [target, map]);
  return null;
}

export function MapPointPicker({ open, lat, lng, onClose, onPick }) {
  const start = lat && lng ? [Number(lat), Number(lng)] : DEFAULT;
  const [center, setCenter] = useState({ lat: start[0], lng: start[1] });
  const [fly, setFly] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const gps = () => {
    if (!navigator.geolocation) return toast.error("Perangkat tidak mendukung GPS");
    setBusy(true);
    navigator.geolocation.getCurrentPosition((p) => { setFly([p.coords.latitude, p.coords.longitude]); setBusy(false); }, () => { toast.error("Gagal mengambil lokasi GPS"); setBusy(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=id&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } });
      const d = await r.json();
      if (!d.length) return toast.error("Lokasi tidak ditemukan");
      setFly([Number(d[0].lat), Number(d[0].lon)]);
    } catch { toast.error("Pencarian gagal, geser peta manual"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="dialog-map-picker">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#F04E23]" /> Tentukan Titik Rumah Server</DialogTitle>
          <DialogDescription>Geser peta hingga penanda merah tepat di atas rumah server, lalu tekan "Pakai Titik Ini". Tidak harus berada di lokasi.</DialogDescription>
        </DialogHeader>
        <form onSubmit={search} className="flex gap-2 px-4">
          <div className="relative flex-1"><Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" /><Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari desa/jalan, mis. Sungai Raya Dalam" data-testid="map-search-input" /></div>
          <Button type="submit" variant="outline" disabled={busy} data-testid="map-search-btn">Cari</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={gps} title="Ke lokasi saya" data-testid="map-gps-btn"><LocateFixed className="w-4 h-4" /></Button>
        </form>
        <div className="relative h-[55vh] mx-4 rounded-xl overflow-hidden border border-border" data-testid="map-picker-canvas">
          <MapContainer center={start} zoom={lat ? 17 : 13} className="h-full w-full" zoomControl>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <CenterTracker onMove={(c) => setCenter({ lat: c.lat, lng: c.lng })} />
            <FlyTo target={fly} />
          </MapContainer>
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-[1000] -translate-x-1/2 -translate-y-full flex flex-col items-center">
            <MapPin className="w-10 h-10 text-[#F04E23] drop-shadow-[0_2px_3px_rgba(0,0,0,.5)]" fill="#F04E23" stroke="white" />
            <Crosshair className="w-4 h-4 text-[#F04E23] -mt-2" />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-4">
          <span className="font-mono text-xs text-muted-foreground" data-testid="map-picker-coords">{center.lat.toFixed(6)}, {center.lng.toFixed(6)}</span>
          <Button type="button" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold" onClick={() => { onPick(center.lat.toFixed(6), center.lng.toFixed(6)); onClose(); }} data-testid="map-picker-use"><MapPin className="w-4 h-4 mr-1.5" /> Pakai Titik Ini</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
